/* eslint-disable @khanacademy/ts-no-error-suppressions */
import {css, StyleSheet} from "aphrodite";
import * as React from "react";
import {useEffect} from "react";

import {
    Breadcrumbs,
    BreadcrumbsItem,
} from "@khanacademy/wonder-blocks-breadcrumbs";
import {View} from "@khanacademy/wonder-blocks-core";
import Link from "@khanacademy/wonder-blocks-link";
import {color} from "@khanacademy/wonder-blocks-tokens";
import {Body, LabelLarge, Title} from "@khanacademy/wonder-blocks-typography";
import {usePublishAnalyticsEvent} from "@khan/analytics-learning-time";
import {useRegionOverride} from "@khan/data-region-override";
import {doNotTranslate, doNotTranslateYet} from "@khan/i18n";
import {RequestInfo} from "@khan/shared-core";
import {mediaQueries} from "@khan/shared-styles";
import {staticUrl} from "@khan/urls";

import type {StandardRegionPresentationsQuery_standardRegionPresentations_presentationSections as PresentationSections} from "./__graphql-types__/StandardRegionPresentationsQuery.ts";
// Components
import {SetOfStandardsPresentationAlignment} from "./set-of-standards-presentation-alignment.tsx";
import type {StandardRegion} from "./types.ts";

const presentationSectionsHaveCourses = (
    presentationSections: Array<PresentationSections>,
): boolean => {
    return presentationSections.some((section) => section.courses?.length > 0);
};

const presentationSectionsHaveStandards = (
    presentationSections: Array<PresentationSections>,
): boolean => {
    return presentationSections.some(
        (section) => section.rootStandards?.length > 0,
    );
};

type Props = {
    presentationSections: Array<PresentationSections>;
    standardRegion: StandardRegion;
};

export const RegionalStandardsPresentationContent = (
    props: Props,
): React.ReactElement => {
    const {presentationSections, standardRegion: region} = props;
    const title = doNotTranslate`${region.name}`;

    // Use presentationSections for content availability checks
    const hasStandards =
        presentationSectionsHaveStandards(presentationSections);
    const hasCourses = presentationSectionsHaveCourses(presentationSections);
    const hasContent = hasStandards || hasCourses;

    const regionOverrideResult = useRegionOverride();
    const publishAnalyticsEvent = usePublishAnalyticsEvent();

    useEffect(() => {
        if (regionOverrideResult.status !== "success") {
            return;
        }
        const referrerHost = document.referrer.length
            ? new URL(document.referrer).hostname
            : null;
        publishAnalyticsEvent({
            eventType: "StandardsRegionPageViewed",
            eventSchemaVersion: 2,
            currentPageRegionCode: region.id,
            referrerDomain: referrerHost,
            standardRegionUserContext_ipGeoRegionCode: RequestInfo.kaRegion,
            standardRegionUserContext_regionCodeSource:
                regionOverrideResult.regionSource,
            standardRegionUserContext_regionCodeUsed:
                regionOverrideResult.regionCode,
        });
    }, [publishAnalyticsEvent, regionOverrideResult, region]);

    let copy;

    if (!hasContent) {
        copy = [
            doNotTranslateYet`We’re sorry, at this time we haven’t aligned our exercises and videos to the ${region.name} standards yet! We are working on it and will update this page once our alignment guides are ready.`,
            doNotTranslateYet`In the meantime, you can explore our library of free courses by clicking the ‘Courses’ button at the top of the page.`,
        ];
    } else if (!hasCourses) {
        copy = [doNotTranslateYet`Browse our content by your state standards.`];
    } else {
        copy = [
            doNotTranslateYet`Check out the courses Khan Academy has built for your state, or browse our content by your state standards.`,
        ];
    }

    const textContent = (
        <React.Fragment>
            {copy.map((paragraph: string, i) => (
                <Body key={i} style={styles.regionCopy}>
                    {paragraph}
                </Body>
            ))}
        </React.Fragment>
    );

    const standardsContent = !hasContent
        ? null
        : presentationSections.map((section) => (
              <SetOfStandardsPresentationAlignment
                  key={section.setOfStandardsId}
                  presentationSection={section}
              />
          ));

    return (
        <View style={styles.background}>
            <View
                style={[
                    styles.regionBlock,
                    !hasContent && styles.regionBlockNoContent,
                ]}
            >
                <View style={styles.contentColumn}>
                    <View style={styles.regionContainer}>
                        <View style={styles.breadcrumbsContainer}>
                            <Breadcrumbs>
                                <BreadcrumbsItem>
                                    <Link
                                        href="/regions/united-states"
                                        style={styles.breadcrumbsLink}
                                    >
                                        {doNotTranslateYet`State Standards`}
                                    </Link>
                                </BreadcrumbsItem>
                                <BreadcrumbsItem>{title}</BreadcrumbsItem>
                            </Breadcrumbs>
                        </View>
                        <View style={styles.regionImageContainer}>
                            <img
                                alt={doNotTranslateYet`Outline of ${title}`}
                                className={css(styles.regionImage)}
                                // @ts-expect-error - TS2769 - No overload matches this call.
                                src={staticUrl(region?.imageUrl)}
                            />
                        </View>
                        <View style={styles.regionCopyContainer}>
                            <Title style={styles.regionTitle}>{title}</Title>
                            {textContent}
                        </View>
                    </View>
                </View>
            </View>
            <View style={styles.contentColumn}>{standardsContent}</View>
            {!hasContent ? null : (
                <React.Fragment>
                    <View style={styles.contentColumn}>
                        <hr className={css(styles.quoteDivider)} />
                    </View>
                    <View style={styles.contentColumn}>
                        <View style={styles.quoteContainer}>
                            <View style={styles.salHeadshotContainer}>
                                <img
                                    alt="Headshot of Sal Khan"
                                    style={styles.salHeadshot}
                                    src={staticUrl(
                                        "/images/regions/region-sal-headshot-opt.png",
                                    )}
                                />
                            </View>
                            <View style={styles.salQuoteContainer}>
                                <Body style={styles.salQuote}>
                                    {doNotTranslate`“It's unacceptable for even one student to not learn. That might be the student who cures cancer for all of us! We want to make sure every human being reaches their potential. The social benefit to society is off the charts for that.”`}
                                </Body>
                                <LabelLarge style={styles.salByline}>
                                    {doNotTranslate`Sal Khan`}
                                    <br />
                                    <em>
                                        {doNotTranslate`Founder and CEO of Khan Academy`}
                                    </em>
                                </LabelLarge>
                            </View>
                        </View>
                    </View>
                </React.Fragment>
            )}
        </View>
    );
};

const regionImageSmallWidth = 288;
const regionImageSmallHeight = 288;
const regionImageMediumWidth = 308;
const regionImageMediumHeight = 308;
const regionImageLargeWidth = 416;
const regionImageLargeHeight = 350;

const styles = StyleSheet.create({
    background: {
        backgroundColor: "#F4EEE2",
        color: color.offBlack,
    },
    breadcrumbsContainer: {
        fontSize: 16,
        lineHeight: "20px",
        [mediaQueries.mdOrLarger]: {
            gridRowStart: "1",
        },
        [mediaQueries.lgOrLarger]: {
            gridColumnStart: "1",
            gridColumnEnd: "3",
        },
        // Hack to make circle svg between breadcrumb items white.
        // @ts-expect-error - TS2322 - Type '{ fontSize: number; lineHeight: string; "@media screen and (min-width: 768px) /* mediaQueries.mdOrLarger */": { gridRowStart: "1"; }; "@media screen and (min-width: 1024px) /* mediaQueries.lgOrLarger */": { ...; }; ":nth-child(1n) svg": { ...; }; ":nth-child(1n) li": { ...; }; }' is not assignable to type 'CSSProperties'.
        ":nth-child(1n) svg": {
            filter: "invert(100%) sepia(0%) saturate(7500%) hue-rotate(182deg) brightness(118%) contrast(118%)",
        },
        // Hack to prevent a:focus-visible outline from rendering only on the right side.
        // Otherwise it is clipped by its parent <li />.
        ":nth-child(1n) li": {
            padding: "2px 0 2px 2px",
        },
    },
    breadcrumbsLink: {
        color: color.white,
        textDecoration: "underline",
        ":focus-visible": {
            color: color.white,
            outline: `1px solid ${color.white}`,
        },
    },
    contentColumn: {
        maxWidth: 1216,
        margin: "auto",
        width: "100%",
        padding: "0px 16px",
    },
    quoteContainer: {
        display: "block",
        marginBottom: 56, // 64px from previous baseline
        padding: "0 16px",
        [mediaQueries.mdOrLarger]: {
            display: "grid",
            gridTemplateColumns: "auto 1fr",
            gridTemplateRows: "auto",
            columnGap: 32,
        },
        [mediaQueries.md]: {
            marginBottom: 104,
        },
        [mediaQueries.lgOrLarger]: {
            marginBottom: 120,
        },
    },
    quoteDivider: {
        boxShadow: "none",
        marginTop: 15, // 32px from previous baseline
        marginBottom: 32,
        [mediaQueries.md]: {
            marginTop: 33, // 72px from previous baseline
            marginBottom: 52,
        },
        [mediaQueries.lgOrLarger]: {
            marginTop: 67, // 120px from previous baseline
            marginBottom: 60,
        },
    },
    regionBlock: {
        backgroundColor: "#2C533C", // Color.green doesn't match
        color: color.white,
        marginBottom: 56, // 64px from bottom edge to next ascent line
        paddingBottom: 39, // 48px from previous baseline to bottom edge.
        paddingTop: 16, // 24px from top edge top next ascent line
        [mediaQueries.mdOrLarger]: {
            paddingTop: 56, // 64px from top edge to next ascent line
        },
        [mediaQueries.md]: {
            marginBottom: 100, // 104px from bottom edge to next ascent line
            paddingBottom: 72,
        },
        [mediaQueries.lgOrLarger]: {
            marginBottom: 115, // 120px from bottom edge to next ascent line
            paddingBottom: 120,
        },
    },
    regionBlockNoContent: {
        marginBottom: 0,
        [mediaQueries.md]: {
            marginBottom: 0,
        },
        [mediaQueries.lgOrLarger]: {
            marginBottom: 0,
        },
    },
    regionContainer: {
        display: "block",
        [mediaQueries.mdOrLarger]: {
            display: "grid",
            gridTemplateColumns: "auto auto",
            gridTemplateRows: "auto 1fr",
        },
        [mediaQueries.md]: {
            columnGap: 24,
        },
        [mediaQueries.lgOrLarger]: {
            columnGap: 114,
        },
    },
    regionCopy: {
        fontSize: 18,
        lineHeight: "32px",
        ":not(:last-child)": {
            marginBottom: 32,
        },
    },
    regionCopyContainer: {
        [mediaQueries.mdOrLarger]: {
            alignSelf: "center",
            gridRowStart: "2",
        },
    },
    regionImage: {
        objectFit: "contain",
        height: "100%",
        width: "100%",
        padding: 36,
        alignSelf: "center",
        justifySelf: "center",

        minWidth: regionImageSmallWidth,
        minHeight: regionImageSmallHeight,
        maxWidth: regionImageLargeWidth,
        maxHeight: regionImageLargeHeight,
        [mediaQueries.md]: {
            maxWidth: regionImageMediumWidth,
            maxHeight: regionImageMediumHeight,
        },
        [mediaQueries.lgOrLarger]: {
            maxWidth: regionImageLargeWidth,
            maxHeight: regionImageLargeHeight,
        },
    },
    regionImageContainer: {
        display: "grid",
        margin: "0 auto",
        minWidth: regionImageSmallWidth,
        minHeight: regionImageSmallHeight,
        maxWidth: regionImageLargeWidth,
        maxHeight: regionImageLargeHeight,
        [mediaQueries.smOrSmaller]: {
            marginBottom: 24,
            marginTop: 16,
        },
        [mediaQueries.mdOrLarger]: {
            gridColumnStart: "2",
            gridColumnEnd: "3",
            alignSelf: "center",
            justifySelf: "center",
        },
        [mediaQueries.md]: {
            width: regionImageMediumWidth,
            height: regionImageMediumHeight,
            gridRowStart: "2",
        },
        [mediaQueries.lgOrLarger]: {
            width: regionImageLargeWidth,
            height: regionImageLargeHeight,
            gridRowStart: "1",
            gridRowEnd: "3",
        },
    },
    regionTitle: {
        fontFamily: "'Source Serif Pro', 'Noto Serif', serif",
        fontSize: 44,
        lineHeight: "48px",
        marginBottom: 3, // 24px from previous baseline to next ascent line
        [mediaQueries.smOrSmaller]: {
            fontSize: 24, // Override more specific selector from @khanacademy/wonder-blocks-typography
            lineHeight: "32px", // Override more specific selector from @khanacademy/wonder-blocks-typography
        },
        [mediaQueries.mdOrLarger]: {
            fontSize: 44, // Override more specific selector from @khanacademy/wonder-blocks-typography
            lineHeight: "48px", // Override more specific selector from @khanacademy/wonder-blocks-typography
        },
        [mediaQueries.lgOrLarger]: {
            marginBottom: 10, // 32px from previous baseline to next ascent line
        },
    },
    salByline: {
        fontSize: 20,
        lineHeight: "30px",
        marginTop: 16, // 32px from previous baseline to next ascent line
        textTransform: "uppercase",
        [mediaQueries.smOrSmaller]: {
            marginTop: 8, // 24px from bottom of image to ascent line
        },
    },
    salHeadshot: {
        objectFit: "contain",
    },
    salHeadshotContainer: {
        margin: "0 auto",
        minWidth: 288,
        minHeight: 256,
        maxWidth: 527,
        maxHeight: 471,
        [mediaQueries.md]: {
            width: 308,
            height: 274,
        },
        [mediaQueries.lgOrLarger]: {
            width: 527,
            height: 471,
        },
    },
    salQuote: {
        fontSize: 18,
        lineHeight: "30px",
    },
    salQuoteContainer: {
        alignSelf: "center",
        [mediaQueries.smOrSmaller]: {
            marginTop: 24, // 32px from bottom of image to ascent line
        },
    },
});
